import { useTheme } from '@emotion/react';
import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  ButtonLink,
  Checkbox,
  Combobox,
  Dialog,
  Flex,
  GridAlpha,
  Heading,
  NumberInput,
  Select,
  createForm,
  useId,
  useToasts,
} from '@applyboard/crystal-ui';
import { useCurrentUser, useEventTracker } from '@applyboard/ui-utils';
import { FULL_COUNTRY_OPTIONS_LIST } from '../../common/constants';
import { useGetReferringRPId } from '../../common/hooks/useQueryParams';
import { getRootUrl } from '../../common/utils/getRootUrl';
import { FxRates } from '../../data/fxRates';
import { Eligibility, useLoanEligibilityChecker } from '../../data/loanEligibility';
import { useGetLoanSupportedSchools } from '../../data/loans';
import { FIELD_OF_STUDY_OPTIONS_LIST } from '../../pages/LoansForm/constants';
import { SUPPORTED_LOAN_CURRENCIES_OPTIONS } from '../../pages/LoansForm/workflow/destination/types';
import { ContactVasSupportDialog } from '../ContactVasSupport';
import { default as LoanEligibilityGauge } from '../LoanEligibilityGauge';
import { VasSpinner } from '../Spinner';

interface LoanEligibilityCriteriaFields {
  nationality: string;
  school: string;
  fieldOfStudy: string;
  loanAmount: string;
  loanAmountCurrency: string;
  tuitionAmount: string;
  tuitionAmountCurrency: string;
  employmentStatus: string;
  annualIncome: string;
  annualIncomeCurrency: string;
  hasCosigner: boolean;
}

const DEFAULT_FORM_VALUE: LoanEligibilityCriteriaFields = {
  nationality: '',
  school: '',
  fieldOfStudy: '',
  loanAmount: '',
  loanAmountCurrency: '',
  tuitionAmount: '',
  tuitionAmountCurrency: '',
  employmentStatus: '',
  annualIncome: '',
  annualIncomeCurrency: '',
  hasCosigner: false,
};

export type LoanEligibilityResultProps = {
  result: Eligibility;
  formValues?: LoanEligibilityCriteriaFields;
};

const LOAN_APPROVAL_RESULT_TITLE = 'Loan Approval Chance';

export const buildLoanApplicationUrl = (
  rpId: string | undefined,
  formValues?: LoanEligibilityCriteriaFields,
) => {
  const baseUrl = getRootUrl() || window.location.origin || '';
  const url = new URL(`${baseUrl}/loan/initiate`);

  url.searchParams.set('source', 'loan_eligibility_checker');
  url.searchParams.set('rp_id', rpId || '');

  if (formValues) {
    if (formValues.nationality) {
      url.searchParams.set('nationality', formValues.nationality);
    }
    if (formValues.school) {
      url.searchParams.set('school_name', formValues.school);
    }
    if (formValues.fieldOfStudy) {
      url.searchParams.set('field_of_study', formValues.fieldOfStudy);
    }
  }

  return url.toString();
};

const LoanEligibilityResult = ({ result, formValues }: LoanEligibilityResultProps) => {
  const { colors } = useTheme();
  const [currentUser] = useCurrentUser();
  const isCurrentUserRp = !!currentUser?.relationships?.agent_information?.data?.id;

  const rpId =
    // Get RP id from query param
    useGetReferringRPId() ||
    // Is current user is a student, then get current user's RP
    (currentUser?.relationships as { agent?: { data: { id: string } } })?.agent?.data?.id ||
    // If current user is an RP then use current user's ID
    isCurrentUserRp
      ? currentUser?.id
      : '';

  let backgroundColor = colors.bgNegative;
  const isHigh = result === 'HIGH';
  const isLow = result === 'LOW';

  if (isHigh) {
    backgroundColor = colors.bgPositive;
  } else if (result === 'MEDIUM') {
    backgroundColor = colors.bgWarning;
  }

  const loanApplicationUrl = buildLoanApplicationUrl(rpId, formValues);

  return (
    <>
      <section
        aria-labelledby="whatsapp-banner-heading"
        style={{
          width: '100%',
          margin: '1rem 0',
          padding: '1rem 1.25rem',
          background: backgroundColor,
          border: 'none',
          borderRadius: '10px',
        }}
      >
        {/** For desktop view*/}
        <Flex
          direction="row"
          justify="center"
          align="center"
          gap={result === 'MEDIUM' ? 14 : 36}
          hideBelow="md"
        >
          <Flex direction="column" pl={8} align="center">
            <Heading level={2} variant="titleL">
              {LOAN_APPROVAL_RESULT_TITLE}
            </Heading>
            <Box pb={4}>
              <LoanEligibilityGauge elibigility={result} />
            </Box>
            <Heading level={4} variant="labelS" intent="warning">
              This is an estimate. It does not guarantee approval.
            </Heading>
          </Flex>
          <Flex direction="row" gap={4}>
            {!isLow ? (
              <ButtonLink
                href={loanApplicationUrl}
                size="md"
                width="hug"
                emphasis="contained"
                intent="primary"
                mode="override_user_choice_and_open_in_new_tab"
                onClick={() => {
                  // Track event for loan application initiation
                }}
              >
                Start Loan Application
              </ButtonLink>
            ) : null}
            {!isHigh ? (
              <ContactVasSupportDialog
                name={currentUser?.full_name ?? ''}
                email={currentUser?.email ?? ''}
                service="Loans"
                vendor="Student"
              />
            ) : null}
          </Flex>
        </Flex>
        {/** For mobile view*/}
        <GridAlpha columns="repeat(12, 1fr)" columnGap={4} rowGap={4} pt={4} hideAbove="md">
          <GridAlpha.Item columnStart={1} columnEnd={13}>
            <Flex direction="column" align="center">
              <Heading level={2} variant="titleL">
                {LOAN_APPROVAL_RESULT_TITLE}
              </Heading>
              <Box pb={4}>
                <LoanEligibilityGauge elibigility={result} />
              </Box>
              <Heading level={4} variant="labelS" intent="warning">
                This is an estimate. It does not guarantee approval.
              </Heading>
            </Flex>
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={1} columnEnd={13}>
            <GridAlpha columns="repeat(12, 1fr)" columnGap={4} rowGap={4}>
              <GridAlpha.Item columnStart={1} columnEnd={4}>
                {!isLow ? (
                  <ButtonLink
                    href={loanApplicationUrl}
                    size="sm"
                    width="hug"
                    emphasis="contained"
                    intent="primary"
                    mode="override_user_choice_and_open_in_new_tab"
                    onClick={() => {
                      // Track event for loan application initiation
                    }}
                  >
                    Start Loan Application
                  </ButtonLink>
                ) : null}
              </GridAlpha.Item>
              <GridAlpha.Item columnStart={1} columnEnd={4}>
                {!isHigh ? (
                  <ContactVasSupportDialog
                    name={currentUser?.full_name ?? ''}
                    email={currentUser?.email ?? ''}
                    service="Loans"
                    vendor="Student"
                    buttonSize="sm"
                    buttonWidth="hug"
                  />
                ) : null}
              </GridAlpha.Item>
            </GridAlpha>
          </GridAlpha.Item>
        </GridAlpha>
      </section>
    </>
  );
};

const { Field, Form } = createForm<LoanEligibilityCriteriaFields>();

export type LoanEligibilityDialogProps = {
  fxRates: FxRates;
  disabled?: boolean;
};

const LoanEligibilityCheckerDialog = ({
  fxRates,
  disabled = false,
}: LoanEligibilityDialogProps) => {
  const formId = useId();
  const [open, setOpen] = useState(false);
  const [eligibility, setEligibility] = useState<Eligibility>();
  const [currentFormValues, setCurrentFormValues] = useState<LoanEligibilityCriteriaFields>();
  const { getEligibilityAsync } = useLoanEligibilityChecker(fxRates);
  const toasts = useToasts();
  const { trackEvent } = useEventTracker();

  const evaluateEligibility = useCallback(
    async (formValues: LoanEligibilityCriteriaFields) => {
      if (formValues.nationality && formValues.loanAmount && formValues.loanAmountCurrency) {
        setCurrentFormValues(formValues);
        getEligibilityAsync(formValues);
        try {
          const result = await getEligibilityAsync(formValues);

          if (result) {
            setEligibility(result);
          }

          trackEvent({
            event: 'Loans Eligibility Checker - Eligibility Result Generated',
            data: {
              eligibility: result,
              fieldValues: formValues,
            },
          });
        } catch (error) {
          console.error('Error checking eligibility:', error);
          toasts.negative(
            new Error('Unable to check loan eligibility at this time. Please try again later.'),
          );
        }
      }
    },
    [getEligibilityAsync, toasts, trackEvent],
  );

  return (
    <>
      <Dialog
        open={open}
        size="xl"
        onOpenChange={open => {
          setOpen(open);
          if (!open) {
            setEligibility(undefined);
            setCurrentFormValues(undefined);
          }
          trackEvent({
            event: `Loans Eligibility Checker - Button Clicked`,
          });
        }}
      >
        <Dialog.Trigger>
          <Button
            size="md"
            width="fill"
            intent="primary"
            emphasis="highlighted"
            disabled={disabled}
            onClick={() => setOpen(true)}
          >
            Check Eligibility
          </Button>
        </Dialog.Trigger>
        <Dialog.Heading>Check Loan Eligibility</Dialog.Heading>
        <Dialog.Content>
          <Flex direction="column" gap={4}>
            {eligibility ? (
              <Flex justify="center" width="hug">
                <LoanEligibilityResult result={eligibility} formValues={currentFormValues} />
              </Flex>
            ) : null}
            <Form
              id={formId}
              defaultValues={DEFAULT_FORM_VALUE}
              onSubmit={values => evaluateEligibility(values)}
            >
              <GridAlpha columns="repeat(27, 1fr)" rowGap={6} pb={6}>
                <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 14 }}>
                  <Field
                    appearance="native"
                    as={Select}
                    name="nationality"
                    label="Nationality"
                    required="Nationality is required"
                    validate={(_, { values }) => {
                      evaluateEligibility(values);
                      return true;
                    }}
                  >
                    {FULL_COUNTRY_OPTIONS_LIST.map(opt => (
                      <Select.Option {...opt} key={opt.value} />
                    ))}
                  </Field>
                </GridAlpha.Item>
                <GridAlpha.Item columnStart={{ xs: 2, md: 15 }} columnEnd={{ xs: 27, md: 26 }}>
                  <SchoolField
                    validate={(_, { values }) => {
                      evaluateEligibility(values);
                      return true;
                    }}
                  />
                </GridAlpha.Item>
                <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 14 }}>
                  <Field
                    appearance="native"
                    as={Select}
                    name="fieldOfStudy"
                    label="Field of Study"
                    validate={(_, { values }) => {
                      evaluateEligibility(values as unknown as LoanEligibilityCriteriaFields);
                      return true;
                    }}
                  >
                    <Select.Option value="" label="Select field of study" />
                    {FIELD_OF_STUDY_OPTIONS_LIST.map(opt => (
                      <Select.Option {...opt} key={opt.value} />
                    ))}
                  </Field>
                </GridAlpha.Item>
                <GridAlpha.Item columnStart={{ xs: 2, md: 15 }} columnEnd={{ xs: 27, md: 26 }}>
                  <Flex gap={2}>
                    <Flex.Item grow={1}>
                      <Field
                        as={NumberInput}
                        name="loanAmount"
                        label="Loan Amount"
                        required="Please enter a loan amount"
                        validate={(_, { values }) => {
                          evaluateEligibility(values);
                          return true;
                        }}
                      />
                    </Flex.Item>
                    <Flex.Item grow={1}>
                      <Field
                        appearance="native"
                        as={Select}
                        aria-label="Currency"
                        label="&nbsp;"
                        name="loanAmountCurrency"
                        placeholder="Currency"
                        validate={(value, { values }) => {
                          if (!value) {
                            return 'Currency is required.';
                          }
                          evaluateEligibility(values);
                          return true;
                        }}
                      >
                        {SUPPORTED_LOAN_CURRENCIES_OPTIONS.map(opt => (
                          <Select.Option {...opt} key={opt.value} />
                        ))}
                      </Field>
                    </Flex.Item>
                  </Flex>
                </GridAlpha.Item>
                <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 14 }}>
                  <Flex gap={2}>
                    <Flex.Item grow={1}>
                      <Field
                        as={NumberInput}
                        name="tuitionAmount"
                        label="Tuition Amount"
                        validate={(value, { values }) => {
                          evaluateEligibility(values);
                          return true;
                        }}
                      />
                    </Flex.Item>
                    <Flex.Item grow={1}>
                      <Field
                        appearance="native"
                        as={Select}
                        aria-label="Currency"
                        label="&nbsp;"
                        name="tuitionAmountCurrency"
                        placeholder="Currency"
                        validate={(value, { values }) => {
                          if (values.tuitionAmount && !value) {
                            return 'Currency is required.';
                          }
                          evaluateEligibility(values);
                          return true;
                        }}
                      >
                        {SUPPORTED_LOAN_CURRENCIES_OPTIONS.map(opt => (
                          <Select.Option {...opt} key={opt.value} />
                        ))}
                      </Field>
                    </Flex.Item>
                  </Flex>
                </GridAlpha.Item>
                <GridAlpha.Item columnStart={{ xs: 2, md: 15 }} columnEnd={{ xs: 27, md: 26 }}>
                  <Field
                    appearance="native"
                    as={Select}
                    label="Employment Status"
                    name="employmentStatus"
                    validate={(_, { values }) => {
                      evaluateEligibility(values);
                      return true;
                    }}
                  >
                    <Select.Option key="employed" label="Employed" value="employed" />
                    <Select.Option key="unemployed" label="Unemployed" value="unemployed" />
                  </Field>
                </GridAlpha.Item>
                <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 14 }}>
                  <Flex gap={2}>
                    <Flex.Item grow={1}>
                      <Field
                        as={NumberInput}
                        name="annualIncome"
                        label="Annual Income"
                        validate={(_, { values }) => {
                          evaluateEligibility(values);
                          return true;
                        }}
                      />
                    </Flex.Item>
                    <Flex.Item grow={1}>
                      <Field
                        appearance="native"
                        as={Select}
                        aria-label="Currency"
                        label="&nbsp;"
                        name="annualIncomeCurrency"
                        placeholder="Currency"
                        validate={(value, { values }) => {
                          if (values.annualIncome && !value) {
                            return 'Currency is required.';
                          }
                          evaluateEligibility(values);
                          return true;
                        }}
                      >
                        {SUPPORTED_LOAN_CURRENCIES_OPTIONS.map(opt => (
                          <Select.Option {...opt} key={opt.value} />
                        ))}
                      </Field>
                    </Flex.Item>
                  </Flex>
                </GridAlpha.Item>
                <GridAlpha.Item columnStart={{ xs: 2, md: 15 }} columnEnd={{ xs: 27, md: 26 }}>
                  <Field
                    as={Checkbox}
                    name="hasCosigner"
                    label="Do you have a cosigner?"
                    validate={(_, { values }) => {
                      evaluateEligibility(values);
                      return true;
                    }}
                  />
                </GridAlpha.Item>
                <GridAlpha.Item columnStart={{ xs: 2, md: 20 }} columnEnd={{ xs: 27, md: 26 }}>
                  <Button intent="primary" width="fill" type="submit" form={formId}>
                    Check Eligibility
                  </Button>
                </GridAlpha.Item>
              </GridAlpha>
            </Form>
          </Flex>
        </Dialog.Content>
      </Dialog>
    </>
  );
};

type SchoolFieldProps = {
  validate?: (value: string, opts: { values: LoanEligibilityCriteriaFields }) => void;
};

function SchoolField({ validate }: SchoolFieldProps) {
  const { trackEvent } = useEventTracker();
  const { supportedSchools, isLoadingSupportedSchools } = useGetLoanSupportedSchools();
  const uniquedSupportedSchools = [...new Set(supportedSchools)];
  const uniqueSupportedSchoolsInLowerCase = uniquedSupportedSchools.map(school =>
    school.toLowerCase(),
  );

  if (isLoadingSupportedSchools) {
    return <VasSpinner />;
  }

  return (
    <>
      <Field
        as={Combobox}
        name="school"
        label="School Name"
        size="md"
        type="text"
        valueMode="input"
        filterOption={(option: { label: string }, query: string) => {
          return option.label.toLowerCase().includes(query.toLowerCase());
        }}
        validate={(value: string, { values }) => {
          if (
            value &&
            values.nationality &&
            values.nationality !== 'IN' &&
            !uniqueSupportedSchoolsInLowerCase.includes(value.toLowerCase())
          ) {
            trackEvent({
              event: 'Loans Eligibility Checker - Invalid School Name',
              data: {
                nationality: values.nationality,
                schoolName: value,
                source: 'loan_eligibility_checker',
              },
            });

            return 'Based on your current nationality and school selection, you are not eligible to proceed with a loan application';
          }

          validate?.(value, { values });
          return true;
        }}
      >
        {uniquedSupportedSchools.map(school => (
          <Combobox.Option key={school} label={school} value={school} />
        ))}
      </Field>
    </>
  );
}

export default LoanEligibilityCheckerDialog;
